const roundDown = (number, decimals) => {
  const factor = 10 ** (Number(decimals) || 0);
  const value = Number(number);
  if (!Number.isFinite(value)) return 0;
  return Math.floor(value * factor + 1e-8) / factor;
};

const digitsOf = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const providerRateDecimal = (rate) => {
  const n = Number(rate);
  if (!Number.isFinite(n) || n === 0) return 0;
  return n > 1 ? n / 100 : n;
};

const treePremiumOf = (premium, treeSplitPercent) => {
  const base = Number(premium) || 0;
  const split = Number(treeSplitPercent);
  const percent = Number.isFinite(split) && split > 0 ? split : 100;
  return (base * percent) / 100;
};

const closeTo = (left, right) => Math.abs(Number(left) - Number(right)) < 0.02;

const modeRate = (values) => {
  const counts = new Map();
  values.forEach((value) => {
    const key = (Math.round(value * 10000) / 10000).toFixed(4);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  let best = null;
  let count = 0;
  counts.forEach((n, key) => {
    if (n > count) {
      best = Number(key);
      count = n;
    }
  });
  return best;
};

const blank = (row) => ({ row, detail: null });

const rateDetail = ({
  row,
  currency,
  premium,
  percent,
  percentDigits,
  exchangeRate,
  ownAmount,
  adjustment,
}) => ({
  row,
  detail: {
    mode: "rate",
    currency,
    premium,
    percent,
    percentDigits,
    exchangeRate,
    ownAmount,
    adjustment,
  },
});

const amountDetail = ({ row, pool, percent, ownAmount, adjustment }) => ({
  row,
  detail: {
    mode: "amount",
    pool,
    percent,
    percentDigits: 2,
    ownAmount,
    adjustment,
  },
});

const explainRateMode = ({
  rows,
  currency,
  treePremium,
  providerRate,
  rateDigits,
  moneyDigits,
  direct,
}) => {
  const samples = [];
  const prepared = rows.map((row) => {
    if (row.index === -1 || !(Number(row.percentage) > 0)) return null;
    const percent = direct
      ? Number(row.percentage)
      : roundDown(Number(row.percentage) * providerRate, rateDigits);
    const actual = percent / 100;
    if (!(actual > 0) || !(Number(row.amount) > 0) || row.isRemainLevel) {
      return { row, percent, actual };
    }
    samples.push(Number(row.amount) / (treePremium * actual));
    return { row, percent, actual };
  });

  const exchangeRate = modeRate(samples.filter((value) => Number.isFinite(value) && value > 0));
  if (!exchangeRate) return null;

  const described = prepared.map((item, index) => {
    const row = rows[index];
    if (!item) return blank(row);
    const ownAmount = roundDown(item.actual * treePremium * exchangeRate, moneyDigits);
    if (!row.isRemainLevel) {
      if (!closeTo(ownAmount, row.amount)) return blank(row);
      return rateDetail({
        row,
        currency,
        premium: treePremium,
        percent: item.percent,
        percentDigits: rateDigits,
        exchangeRate,
        ownAmount,
        adjustment: 0,
      });
    }
    const adjustment = Number((Number(row.amount) - ownAmount).toFixed(2));
    return rateDetail({
      row,
      currency,
      premium: treePremium,
      percent: item.percent,
      percentDigits: rateDigits,
      exchangeRate,
      ownAmount,
      adjustment,
    });
  });

  const eligibleIndexes = rows
    .map((row, index) =>
      row.index !== -1 && !row.isRemainLevel && Number(row.percentage) > 0 ? index : -1
    )
    .filter((index) => index >= 0);
  const failed = eligibleIndexes.filter((index) => !described[index].detail);
  if (!eligibleIndexes.length || failed.length > 1) return null;
  if (failed.length === 1) {
    const index = failed[0];
    const item = prepared[index];
    const ownAmount = roundDown(item.actual * treePremium * exchangeRate, moneyDigits);
    described[index] = rateDetail({
      row: rows[index],
      currency,
      premium: treePremium,
      percent: item.percent,
      percentDigits: rateDigits,
      exchangeRate,
      ownAmount,
      adjustment: Number((Number(rows[index].amount) - ownAmount).toFixed(2)),
    });
  }
  return described;
};

const productDetail = ({
  row,
  currency,
  premium,
  providerPercent,
  exchangeRate,
  levelPercent,
  ownAmount,
  adjustment,
}) => ({
  row,
  detail: {
    mode: "product",
    currency,
    premium,
    providerPercent,
    exchangeRate,
    levelPercent,
    ownAmount,
    adjustment,
  },
});

const explainProductMode = ({
  rows,
  currency,
  treePremium,
  providerRate,
  exchangeRate,
  moneyDigits,
}) => {
  if (!(treePremium > 0) || !(providerRate > 0) || !(exchangeRate > 0)) return null;
  const providerPercent = providerRate * 100;
  const described = rows.map((row) => {
    if (row.index === -1 || !(Number(row.percentage) > 0)) return blank(row);
    const ownAmount = roundDown(
      treePremium * providerRate * exchangeRate * (Number(row.percentage) / 100),
      moneyDigits
    );
    if (!row.isRemainLevel && !closeTo(ownAmount, row.amount)) return blank(row);
    const adjustment = row.isRemainLevel
      ? Number((Number(row.amount) - ownAmount).toFixed(2))
      : 0;
    return productDetail({
      row,
      currency,
      premium: treePremium,
      providerPercent,
      exchangeRate,
      levelPercent: Number(row.percentage),
      ownAmount,
      adjustment,
    });
  });

  const eligibleIndexes = rows
    .map((row, index) =>
      row.index !== -1 && !row.isRemainLevel && Number(row.percentage) > 0 ? index : -1
    )
    .filter((index) => index >= 0);
  const failed = eligibleIndexes.filter((index) => !described[index].detail);
  if (!eligibleIndexes.length || failed.length > 1) return null;
  if (failed.length === 1) {
    const index = failed[0];
    const ownAmount = roundDown(
      treePremium * providerRate * exchangeRate * (Number(rows[index].percentage) / 100),
      moneyDigits
    );
    described[index] = productDetail({
      row: rows[index],
      currency,
      premium: treePremium,
      providerPercent,
      exchangeRate,
      levelPercent: Number(rows[index].percentage),
      ownAmount,
      adjustment: Number((Number(rows[index].amount) - ownAmount).toFixed(2)),
    });
  }
  return described;
};

const explainAmountMode = ({ rows, pool, moneyDigits }) => {
  if (!(Number(pool) > 0)) return null;
  const described = rows.map((row) => {
    if (row.index === -1 || !(Number(row.percentage) > 0)) return blank(row);
    const ownAmount = roundDown((Number(pool) * Number(row.percentage)) / 100, moneyDigits);
    if (!row.isRemainLevel && !closeTo(ownAmount, row.amount)) return blank(row);
    const adjustment = row.isRemainLevel
      ? Number((Number(row.amount) - ownAmount).toFixed(2))
      : 0;
    if (!row.isRemainLevel && !closeTo(ownAmount, row.amount)) return blank(row);
    return amountDetail({
      row,
      pool: Number(pool),
      percent: Number(row.percentage),
      ownAmount,
      adjustment,
    });
  });

  const matched = described.filter((item) => item.detail && !item.row.isRemainLevel).length;
  const eligible = rows.filter(
    (row) => row.index !== -1 && !row.isRemainLevel && Number(row.percentage) > 0
  ).length;
  if (!eligible || matched < eligible) return null;
  return described;
};

/**
 * Explain each saved overriding row with the calculation that produced it.
 * The level exchange rate is read back from the saved amounts, so a later
 * change to the exchange-rate table does not rewrite an old order.
 */
export const describeOverridingRowAmounts = ({
  premium,
  currency = "HKD",
  providerRate,
  providerExchangeRate,
  internalExchangeRate,
  treeSplitPercent = 100,
  pool,
  plan,
  rows = [],
  splitByAmount = false,
  splitByCommissionRate = false,
} = {}) => {
  if (splitByAmount || splitByCommissionRate || !Array.isArray(rows) || rows.length === 0) {
    return rows.map((row) => blank(row));
  }

  const treePremium = treePremiumOf(premium, treeSplitPercent);
  const rate = providerRateDecimal(providerRate);
  const rateDigits = digitsOf(plan?.rateRoundDigit, 2);
  const moneyDigits = digitsOf(plan?.moneyRoundDigit, 2);
  const direct = Boolean(plan?.allowSkipProviderRate);
  const roundOnRate = Boolean(plan?.roundOnRate) && !direct;

  if (!(treePremium > 0)) return rows.map((row) => blank(row));

  const providerFx = Number(providerExchangeRate);
  const internalFx = Number(internalExchangeRate);
  if (!roundOnRate && !direct && rate > 0) {
    const selectedFx =
      Boolean(plan?.useInternalExchangeRate) && providerFx > internalFx && internalFx > 0
        ? internalFx
        : providerFx > 0
          ? providerFx
          : 0;
    const fxCandidates = selectedFx > 0 ? [selectedFx] : [];
    const inferredFx = modeRate(
      rows
        .filter(
          (row) =>
            row.index !== -1 &&
            !row.isRemainLevel &&
            Number(row.percentage) > 0 &&
            Number(row.amount) > 0
        )
        .map((row) => row.amount / (treePremium * rate * (Number(row.percentage) / 100)))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
    if (inferredFx > 0 && !fxCandidates.includes(inferredFx)) fxCandidates.push(inferredFx);
    for (const exchangeRate of fxCandidates) {
      const explained = explainProductMode({
        rows,
        currency: currency || "HKD",
        treePremium,
        providerRate: rate,
        exchangeRate,
        moneyDigits,
      });
      if (explained) return explained;
    }
  }

  if (roundOnRate || direct) {
    const explained = explainRateMode({
      rows,
      currency: currency || "HKD",
      treePremium,
      providerRate: rate,
      rateDigits,
      moneyDigits,
      direct,
    });
    if (explained) return explained;
  }

  const byAmount = explainAmountMode({ rows, pool, moneyDigits });
  if (byAmount) return byAmount;

  return rows.map((row) => blank(row));
};
